<script>
import { mapState, mapActions, mapGetters } from "vuex";

import InfoTooltip from "@/components/InfoTooltip";
import AutoRefreshToggle from "@/components/AutoRefreshToggle";
import HashrateChart from "@/components/HashrateChart";
import WorkerList from "@/components/WorkerList";
import NavigationWrapper from "@/components/NavigationWrapper";
import AlertEvents from "@/components/AlertEvents";
import AlertHeatmap from "@/components/AlertHeatmap";
import DeleteGroupModal from "@/pages/Groups/Group/DeleteModal";
import { formatHashrate, getBreakpoint } from "@/utils";

export default {
  // Included components
  components: {
    NavigationWrapper,
    HashrateChart,
    InfoTooltip,
    AlertEvents,
    AlertHeatmap,
    WorkerList,
    DeleteGroupModal,
    AutoRefreshToggle,
  },

  data() {
    return {
      // The group ID of the group being displayed
      groupId: null,
    };
  },

  computed: {
    // The alerts for this group
    groupAlerts() {
      return Object.values(this.alerts).filter((alert) => {
        return alert.group_id == this.groupId;
      });
    },

    // Total number of alerts in this group
    alertsCount() {
      return this.groupAlerts.length;
    },

    // Count of alerts that are triggering
    triggeringAlertsCount() {
      return this.groupAlerts.filter(
        (alert) => alert.triggering && alert.enabled
      ).length;
    },

    // The group statistics
    statistics() {
      return this.groupStatistics[this.groupId];
    },

    // The group hashrate data
    hashrate() {
      if (this.groupShares == null) return 0;
      return this.groupShares[this.groupId].map((share) => [
        share[0],
        share[1] / 300, // 5 minutes
      ]);
    },

    // The actual group
    group() {
      return this.groups[this.groupId];
    },

    // Other state mappings
    ...mapGetters(["can"]),
    ...mapState([
      "groups",
      "activeTeamId",
      "groupStatistics",
      "groupShares",
      "alerts",
    ]),
  },

  // On creation fetch the group metadata, statistics, and hashrate
  async created() {
    this.groupId = this.$route.params.groupId;

    // Get this current group
    await this.getGroup({ groupId: this.groupId });

    // If this group's team ID is not the active team ID, then go back to groups page
    if (this.group.team_id !== this.activeTeamId) {
      await this.$router.push("/groups");
      return;
    }

    // Parallelize fetch group information
    await Promise.all([
      this.updateHashrateChartData(),
      this.updateStatistics(),
    ]);
  },

  methods: {
    // Update the hashrate chart data (every 5 minutes if autorefresh is on)
    async updateHashrateChartData() {
      await this.getGroupShares({
        groupId: this.groupId,
      });
    },

    // Update the group statistics (every 30 seconds if autorefresh is on)
    async updateStatistics() {
      let fetches = [this.getGroupStatistics({ groupId: this.groupId })];
      if (this.can.alerts.view) {
        fetches.push(this.getAlerts({}));
      }
      await Promise.all(fetches);
    },

    // Helper methods and mapped vuex actions
    formatHashrate,
    getBreakpoint,
    ...mapActions([
      "getGroup",
      "getGroupStatistics",
      "getGroupShares",
      "getAlerts",
    ]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/groups">Groups</b-breadcrumb-item>
      <b-breadcrumb-item active>{{ group.name }}</b-breadcrumb-item>
    </template>
    <b-row class="mb-3">
      <b-col cols="7">
        <h1>{{ group.name }}</h1>
        <AutoRefreshToggle
          v-if="getBreakpoint().xs || getBreakpoint().sm"
          @update-5m="updateHashrateChartData()"
          @update-30s="updateStatistics()"
        />
      </b-col>
      <b-col cols="5" :align-self="getBreakpoint().xs ? '' : 'center'">
        <div :class="getBreakpoint().xs ? '' : 'float-right'">
          <AutoRefreshToggle
            v-if="!(getBreakpoint().xs || getBreakpoint().sm)"
            @update-5m="updateHashrateChartData()"
            @update-30s="updateStatistics()"
          />
          <span v-if="can.groups.edit" class="d-none d-sm-flex">
            <b-button class="ml-3 mr-3" variant="danger" v-b-modal.delete-modal>
              Delete
            </b-button>
            <DeleteGroupModal :group-id="groupId" modal-id="delete-modal" />
            <b-button
              class="float-right"
              variant="primary"
              :to="'/groups/' + groupId + '/edit'"
            >
              Edit Group
            </b-button>
          </span>
          <span v-if="can.groups.edit" class="d-flex d-sm-none">
            <b-dropdown class="mt-2" text="Actions" variant="primary">
              <b-dropdown-item v-b-modal.delete-modal>Delete</b-dropdown-item>
              <DeleteGroupModal :group-id="groupId" modal-id="delete-modal" />
              <b-dropdown-item :to="'/groups/' + groupId + '/edit'">
                Edit group
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </div>
      </b-col>
      <b-col cols="12" v-if="group.notes" class="Group-notes">
        {{ group.notes }}
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card class="mb-3">
          <b-row>
            <b-col cols="12" :sm="can.alerts.view ? 4 : 6" class="mb-2 mb-sm-0">
              <div class="Group-stat">
                <b>Hashrate</b>
                &nbsp;
                <InfoTooltip text="30 minute average hashrate" />
                <div class="Group-stat-value">
                  {{ formatHashrate(statistics.hashrate) }}
                </div>
              </div>
            </b-col>
            <b-col cols="12" :sm="can.alerts.view ? 4 : 6" class="mb-2 mb-sm-0">
              <div class="Group-stat">
                <b>Active Workers</b>
                <div class="Group-stat-value">
                  {{ statistics.active_count }} / {{ statistics.count }}
                </div>
              </div>
            </b-col>
            <b-col v-if="can.alerts.view" cols="12" sm="4" class="mb-2 mb-sm-0">
              <div class="Group-stat">
                <b>Alert Status</b>
                <div class="Group-stat-value">
                  <span v-if="triggeringAlertsCount > 0">
                    <b-badge variant="danger">ALERT</b-badge>
                    &times;
                    {{ triggeringAlertsCount }}
                  </span>
                  <b-badge v-else variant="success">OK</b-badge>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
        <b-card no-body class="mb-3">
          <b-card-body class="p-2 p-md-3">
            <HashrateChart
              :axis-titles="false"
              :data="hashrate"
              :tick-amount="5"
              :canExport="true"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <WorkerList
      :groupId="this.groupId"
      :inactive="true"
      v-if="can.workers.view"
      class="mb-3"
    />
    <b-row class="mb-3" v-if="can.alerts.view">
      <b-col>
        <h3 class="mb-3">Alert Events</h3>
        <AlertHeatmap :group-id="groupId" class="mb-3"></AlertHeatmap>
        <AlertEvents :group-id="groupId"></AlertEvents>
      </b-col>
    </b-row>
    <WorkerList :group-id="groupId" v-if="can.workers.view" />
  </NavigationWrapper>
</template>

<style scoped>
.Group-stat {
  text-align: center;
}

.Group-stat-value {
  font-size: 1.4rem;
}

.Group-notes {
  color: #6c757d;
}
</style>
